import { useState } from 'react'
import { WA_NUMBER, WA_MESSAGES } from '../../constants'
import { reviews } from '../../data/reviews'
import Lightbox from '../Lightbox'

const PUBLIC = filename => `${process.env.PUBLIC_URL}/reviews/${filename}`

function initials(name = '') {
  return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
}

function Stars({ value, size = 13 }) {
  return (
    <span style={{ color: 'var(--color-accent-warm)', fontSize: `${size}px`, letterSpacing: '2px' }} aria-label={`${value} out of 5 stars`}>
      {'★'.repeat(Math.round(value))}<span style={{ color: 'var(--stone-mid)' }}>{'★'.repeat(5 - Math.round(value))}</span>
    </span>
  )
}

function Avatar({ review }) {
  const base = { width: 40, height: 40, borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }
  if (review.photo) {
    return <img src={PUBLIC(review.photo)} alt={review.name} style={base} />
  }
  return (
    <div aria-hidden="true" style={{
      ...base, background: 'rgba(219,160,90,0.16)', color: 'var(--color-text)',
      border: '1px solid rgba(219,160,90,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: 600,
    }}>
      {initials(review.name)}
    </div>
  )
}

const TEXT_CLAMP = 220

const thumbBtn = { padding: 0, border: 'none', background: 'none', cursor: 'pointer', flexShrink: 0, display: 'flex' }

function ReviewRow({ review, trek, waHref, onPhotoClick }) {
  const [expanded, setExpanded] = useState(false)
  const [helpful, setHelpful] = useState(review.helpful || 0)
  const [voted, setVoted] = useState(false)

  const long = review.text.length > TEXT_CLAMP
  const shownText = !long || expanded ? review.text : `${review.text.slice(0, TEXT_CLAMP).trimEnd()}…`

  return (
    <div style={{ borderTop: '1px solid var(--stone-mid)', padding: '24px 0' }}>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
        <Avatar review={review} />
        <div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: 600 }}>{review.name}</div>
          <div className="data" style={{ fontSize: '11px', color: 'var(--color-accent-cool)', marginTop: '2px' }}>
            {review.country}{review.date ? ` · ${review.date}` : ''}
          </div>
          {review.source && (
            <div style={{ fontSize: '10px', color: 'var(--color-text-secondary)', marginTop: '3px' }}>
              ✓ Verified on {review.source}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
        {review.rating ? <Stars value={review.rating} /> : null}
        {review.title && <span style={{ fontSize: '14px', fontWeight: 600 }}>{review.title}</span>}
      </div>

      <div style={{ fontSize: '11px', color: 'var(--ink-light)', marginBottom: '10px' }}>
        Review for: {trek.name}
        {' · '}
        <a href={waHref} target="_blank" rel="noreferrer" style={{ color: 'var(--green)', fontWeight: 600 }}>
          Book Now
        </a>
      </div>

      <p style={{ fontSize: '13px', lineHeight: 1.7, color: 'var(--ink-mid)', fontWeight: 300 }}>
        {shownText}
      </p>
      {long && (
        <button
          onClick={() => setExpanded(e => !e)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green)', fontSize: '12px', fontWeight: 600, padding: 0, marginTop: '4px' }}
        >
          {expanded ? 'See less' : 'See more'}
        </button>
      )}

      {review.photos && review.photos.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px', overflowX: 'auto' }}>
          {review.photos.map((p, i) => (
            <button key={`${p}-${i}`} onClick={() => onPhotoClick(i)} aria-label={`View enlarged: from ${review.name}'s trek`} style={thumbBtn}>
              <img src={PUBLIC(p)} alt={`From ${review.name}'s trek`} style={{
                width: '72px', height: '72px', objectFit: 'cover', borderRadius: 'var(--radius)',
              }} />
            </button>
          ))}
        </div>
      )}

      <button
        onClick={() => { if (!voted) { setHelpful(h => h + 1); setVoted(true) } }}
        style={{
          marginTop: '14px', background: 'none', cursor: voted ? 'default' : 'pointer',
          border: '1px solid var(--stone-mid)', borderRadius: '40px', padding: '5px 14px',
          fontSize: '11px', color: voted ? 'var(--green)' : 'var(--ink-mid)',
        }}
      >
        👍 Helpful ({helpful})
      </button>
    </div>
  )
}

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'photos', label: 'With photos' },
  { key: '4+', label: '4.0+' },
  { key: '3+', label: '3.0+' },
  { key: '<3', label: '< 3.0' },
]

export default function TrekReviewsPanel({ trek }) {
  const [filter, setFilter] = useState('all')
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [lightbox, setLightbox] = useState(null)

  const trekReviews = reviews.filter(r => r.trek === trek.name)

  // Each review's starting position in the pooled photo list, so a click on a
  // review thumbnail opens the lightbox at the right global index.
  const photoOffsets = new Map()
  let offset = 0
  trekReviews.forEach(r => {
    photoOffsets.set(r.id, offset)
    offset += (r.photos || []).length
  })
  const rated = trekReviews.filter(r => r.rating)
  const avg = rated.length ? rated.reduce((s, r) => s + r.rating, 0) / rated.length : null

  const waMessage = WA_MESSAGES[trek.waKey] || WA_MESSAGES.general
  const waHref = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(waMessage)}`

  const allPhotos = trekReviews.flatMap(r => r.photos || [])
  const galleryPhotos = galleryOpen ? allPhotos : allPhotos.slice(0, 5)

  const filtered = trekReviews.filter(r => {
    if (filter === 'photos') return r.photos && r.photos.length > 0
    if (filter === '4+') return (r.rating || 0) >= 4
    if (filter === '3+') return (r.rating || 0) >= 3
    if (filter === '<3') return (r.rating || 0) < 3
    return true
  })

  if (!trekReviews.length) return null

  return (
    <div>
      <h2 className="section-title" style={{ fontSize: '26px', marginBottom: '20px' }}>Reviews.</h2>

      {/* Score header */}
      {avg !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <span style={{ fontFamily: 'var(--serif)', fontSize: '40px', fontWeight: 600, lineHeight: 1 }}>
            {Number.isInteger(avg) ? avg : avg.toFixed(1)}<span style={{ fontSize: '18px', color: 'var(--ink-light)' }}>/5</span>
          </span>
          <div>
            <Stars value={avg} size={16} />
            <div style={{ fontSize: '12px', color: 'var(--ink-light)', marginTop: '2px' }}>
              Based on {trekReviews.length} review{trekReviews.length > 1 ? 's' : ''}
            </div>
          </div>
        </div>
      )}

      {/* Photo gallery strip */}
      {allPhotos.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>
            Photo gallery <span style={{ color: 'var(--ink-light)', fontWeight: 400 }}>({allPhotos.length} photo{allPhotos.length > 1 ? 's' : ''})</span>
          </div>
          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
            {galleryPhotos.map((p, i) => (
              <button key={`${p}-${i}`} onClick={() => setLightbox(i)} aria-label={`View gallery item ${i + 1} enlarged`} style={thumbBtn}>
                <img src={PUBLIC(p)} alt={`Gallery item ${i + 1}`} style={{
                  width: '96px', height: '96px', objectFit: 'cover', borderRadius: 'var(--radius)',
                }} />
              </button>
            ))}
          </div>
          {allPhotos.length > 5 && (
            <button
              onClick={() => setGalleryOpen(o => !o)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green)', fontSize: '12px', fontWeight: 600, padding: 0, marginTop: '8px' }}
            >
              {galleryOpen ? 'See less' : 'See more'}
            </button>
          )}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
        {FILTERS.map(f => (
          <button
            key={f.key}
            className={`pill-tab ${filter === f.key ? 'active' : ''}`}
            style={{ padding: '6px 14px', fontSize: '12px' }}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Review list */}
      {filtered.length ? (
        filtered.map(r => (
          <ReviewRow
            key={r.id}
            review={r}
            trek={trek}
            waHref={waHref}
            onPhotoClick={i => setLightbox(photoOffsets.get(r.id) + i)}
          />
        ))
      ) : (
        <p style={{ fontSize: '13px', color: 'var(--ink-light)', padding: '24px 0', borderTop: '1px solid var(--stone-mid)' }}>
          No reviews match this filter.
        </p>
      )}

      {lightbox != null && (
        <Lightbox
          images={allPhotos.map(PUBLIC)}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onNavigate={setLightbox}
        />
      )}
    </div>
  )
}
